import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Tag,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';
import { Solution } from '../../data/solutions';

const MotionBox = motion(Box);

interface SolutionShowcaseProps {
  solution: Solution;
}

export const SolutionShowcase = ({ solution }: SolutionShowcaseProps) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconColor = useColorModeValue('brand.500', 'brand.300');
  const tagBg = useColorModeValue('brand.50', 'brand.900');
  const tagColor = useColorModeValue('brand.700', 'brand.200');
  const checkColor = useColorModeValue('green.500', 'green.300');
  const mutedText = useColorModeValue('gray.600', 'gray.400');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        bg={cardBg}
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        p={{ base: 6, md: 8 }}
        shadow="sm"
      >
        <VStack align="start" spacing={6}>
          {/* Header */}
          <Flex align="center" gap={4}>
            <Box
              p={3}
              borderRadius="lg"
              bg={useColorModeValue('brand.50', 'brand.900')}
            >
              <Icon as={solution.icon} w={8} h={8} color={iconColor} />
            </Box>
            <Box>
              <Heading as="h3" size="lg">
                {solution.title}
              </Heading>
              <Text color={mutedText} fontStyle="italic">
                {solution.tagline}
              </Text>
            </Box>
          </Flex>

          {/* Description */}
          <VStack align="start" spacing={4}>
            {solution.description.map((paragraph, index) => (
              <Text key={index} color={mutedText} lineHeight="tall">
                {paragraph}
              </Text>
            ))}
          </VStack>

          {/* Features */}
          <Box w="full">
            <Heading as="h4" size="sm" mb={4} color={mutedText}>
              Key Capabilities
            </Heading>
            <List spacing={3}>
              {solution.features.map((feature, index) => (
                <ListItem key={index} display="flex" alignItems="flex-start">
                  <ListIcon as={FaCheckCircle} color={checkColor} mt={1} />
                  <Text>{feature}</Text>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Technologies */}
          <Box w="full">
            <Heading as="h4" size="sm" mb={3} color={mutedText}>
              Technologies
            </Heading>
            <HStack wrap="wrap" spacing={2}>
              {solution.technologies.map((tech) => (
                <Tag
                  key={tech}
                  size="md"
                  bg={tagBg}
                  color={tagColor}
                  borderRadius="full"
                >
                  {tech}
                </Tag>
              ))}
            </HStack>
          </Box>
        </VStack>
      </Box>
    </MotionBox>
  );
};
